import { AppData, DeveloperData, StoreType, UnifiedAppMetadata } from '../types';
import { APPS_DATA, DEVELOPERS_DATA } from '../data/mockData';

export interface StorefrontCountry {
  code: string;
  name: string;
  flag: string;
}

export const STOREFRONT_COUNTRIES: StorefrontCountry[] = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' }
];

export interface QueryAppsParams {
  q?: string;
  country?: string;
  category?: string;
  store?: StoreType;
  sort?: 'trending' | 'top_ranked' | 'highest_rated' | 'most_reviewed' | 'recently_updated' | 'recently_discovered' | 'growth' | 'rank' | 'rating' | 'downloads';
  page?: number;
  limit?: number;
}

export interface QueryAppsResponse {
  results: AppData[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  totalIndexed?: number;
  databaseEngine?: string;
}

export interface CategoryResponse {
  category: string;
  slug: string;
  description: string;
  apps: AppData[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  uniqueDevelopersCount?: number;
  source?: string;
  databaseEngine?: string;
  liveRefreshAvailable?: boolean;
}

export interface CatalogStats {
  database?: string;
  databaseEngine?: string;
  connectionStatus?: string;
  appsIndexed: number;
  developersIndexed: number;
  storefrontRecords?: number;
  rankingObservationsTotal?: number;
  rankingObservationsByType?: {
    search_position: number;
    top_free: number;
    top_paid: number;
    top_grossing: number;
  };
  estimatedMetricsCount?: number;
  developerVerifiedMetricsCount?: number;
  countriesTracked: number;
  reviewsAnalyzed?: number;
  searchesPerformed: number;
  appleSearchesPerformed: number;
  newAppsDiscovered: number;
  duplicatesPrevented: number;
  searchCacheHits: number;
  metadataCacheHits: number;
  failedRequests: number;
  rateLimitStatus: string;
  requestsTotal?: number;
  requestsLastMinute?: number;
  successfulRequests?: number;
  rateLimitedRequests?: number;
  averageLatencyMs?: number;
  last429At?: string | null;
  lastIngestionAt: string;
  dbSizeBytes?: number;
}

// Client cache map
const clientAppCache = new Map<string, { data: AppData; timestamp: number }>();
const CLIENT_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

class AppCatalogService {
  private selectedCountry: string = 'US';

  public getSelectedCountry(): string {
    return this.selectedCountry;
  }

  public setSelectedCountry(code: string) {
    this.selectedCountry = code.toUpperCase();
  }

  /**
   * Search and browse apps with pagination, filtering, and database-first retrieval
   */
  async searchApps(params: QueryAppsParams = {}): Promise<QueryAppsResponse> {
    const {
      q = '',
      country = this.selectedCountry,
      category = 'all',
      store = 'all',
      sort = 'trending',
      page = 1,
      limit = 50
    } = params;

    try {
      const queryParams = new URLSearchParams({
        q: q.trim(),
        country,
        category,
        store,
        sort,
        page: String(page),
        limit: String(limit)
      });

      const res = await fetch(`/api/apps?${queryParams.toString()}`);
      if (res.ok) {
        const data: QueryAppsResponse = await res.json();
        if (data.results && Array.isArray(data.results)) {
          data.results.forEach((app) => {
            clientAppCache.set(app.id, { data: app, timestamp: Date.now() });
            if (app.slug) clientAppCache.set(app.slug, { data: app, timestamp: Date.now() });
            if (app.appStoreId) clientAppCache.set(String(app.appStoreId), { data: app, timestamp: Date.now() });
          });
        }
        return data;
      }
    } catch (err) {
      console.warn('[AppCatalogService] Server fetch error, falling back to local fallback:', err);
    }

    const mockList = Object.values(APPS_DATA);
    let filtered = mockList.filter((a) => {
      if (store !== 'all' && !a.stores.includes(store as any)) return false;
      if (category !== 'all' && a.categorySlug !== category) return false;
      if (q.trim()) {
        const cleanQ = q.toLowerCase().trim();
        return (
          a.name.toLowerCase().includes(cleanQ) ||
          a.developer.toLowerCase().includes(cleanQ) ||
          a.category.toLowerCase().includes(cleanQ)
        );
      }
      return true;
    });

    return {
      results: filtered.slice((page - 1) * limit, page * limit),
      total: filtered.length,
      page,
      limit,
      hasMore: page * limit < filtered.length,
      totalIndexed: mockList.length
    };
  }

  /**
   * Fetch complete AppData for any app ID, slug, or App Store ID
   */
  async getApp(idOrSlug: string, country: string = this.selectedCountry): Promise<AppData | null> {
    const clean = idOrSlug.trim();
    if (!clean) return null;

    const cached = clientAppCache.get(clean);
    if (cached && Date.now() - cached.timestamp < CLIENT_CACHE_TTL) {
      return cached.data;
    }

    try {
      const res = await fetch(`/api/apps/${encodeURIComponent(clean)}?country=${encodeURIComponent(country)}`);
      if (res.ok) {
        const app: AppData = await res.json();
        clientAppCache.set(clean, { data: app, timestamp: Date.now() });
        clientAppCache.set(app.id, { data: app, timestamp: Date.now() });
        if (app.slug) clientAppCache.set(app.slug, { data: app, timestamp: Date.now() });
        if (app.appStoreId) clientAppCache.set(String(app.appStoreId), { data: app, timestamp: Date.now() });
        return app;
      }
    } catch (err) {
      console.warn(`[AppCatalogService] Error fetching app ${clean}:`, err);
    }

    const local = APPS_DATA[clean] || Object.values(APPS_DATA).find((a) => a.id === clean || a.slug === clean || String(a.appStoreId) === clean);
    if (local) return local;

    return null;
  }

  /**
   * Fetch developer profile with their apps
   */
  async getDeveloper(slugOrId: string): Promise<(DeveloperData & { apps?: AppData[] }) | null> {
    const clean = slugOrId.trim();
    if (!clean) return null;

    try {
      const res = await fetch(`/api/developers/${encodeURIComponent(clean)}`);
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.warn(`[AppCatalogService] Error fetching developer ${clean}:`, err);
    }

    const localDev = DEVELOPERS_DATA[clean];
    if (localDev) {
      const apps = localDev.appIds.map((id) => APPS_DATA[id]).filter(Boolean);
      return { ...localDev, apps };
    }

    return null;
  }

  /**
   * Get all developers with pagination
   */
  async getDevelopers(q = '', page = 1, limit = 50): Promise<{ results: DeveloperData[]; total: number; hasMore: boolean }> {
    try {
      const res = await fetch(`/api/developers?q=${encodeURIComponent(q)}&page=${page}&limit=${limit}`);
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.warn('[AppCatalogService] Error fetching developers:', err);
    }

    const all = Object.values(DEVELOPERS_DATA);
    return {
      results: all.slice((page - 1) * limit, page * limit),
      total: all.length,
      hasMore: page * limit < all.length
    };
  }

  /**
   * Universal getApps method compatible with both params object and positional arguments
   */
  async getApps(paramsOrQ: QueryAppsParams | string = {}, category = 'all', store: StoreType = 'all', sort = 'trending', page = 1, limit = 50): Promise<QueryAppsResponse> {
    if (typeof paramsOrQ === 'object' && paramsOrQ !== null) {
      return this.searchApps(paramsOrQ);
    }
    return this.searchApps({
      q: typeof paramsOrQ === 'string' ? paramsOrQ : '',
      category,
      store,
      sort: sort as any,
      page,
      limit
    });
  }

  /**
   * Fetch category-specific apps from Supabase PostgreSQL first
   */
  async getCategory(slug: string, params: QueryAppsParams = {}): Promise<CategoryResponse> {
    const {
      q = '',
      country = this.selectedCountry,
      store = 'all',
      sort = 'trending',
      page = 1,
      limit = 50
    } = params;

    const cleanSlug = slug.trim().toLowerCase();

    try {
      const queryParams = new URLSearchParams({
        q: q.trim(),
        country,
        store,
        sort,
        page: String(page),
        limit: String(limit)
      });

      const res = await fetch(`/api/categories/${encodeURIComponent(cleanSlug)}?${queryParams.toString()}`);
      if (res.ok) {
        const data: CategoryResponse = await res.json();
        if (data.apps && Array.isArray(data.apps)) {
          data.apps.forEach((app) => {
            clientAppCache.set(app.id, { data: app, timestamp: Date.now() });
            if (app.slug) clientAppCache.set(app.slug, { data: app, timestamp: Date.now() });
            if (app.appStoreId) clientAppCache.set(String(app.appStoreId), { data: app, timestamp: Date.now() });
          });
        }
        return data;
      }
    } catch (err) {
      console.warn(`[AppCatalogService] Server error fetching category ${cleanSlug}, using cached fallback:`, err);
    }

    // Client fallback from searchApps
    const fallbackResults = await this.searchApps({
      q,
      country,
      category: cleanSlug,
      store,
      sort,
      page,
      limit
    });

    const prettyName = cleanSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    return {
      category: prettyName,
      slug: cleanSlug,
      description: `Applications indexed in the ${prettyName} category.`,
      apps: fallbackResults.results,
      total: fallbackResults.total,
      page: fallbackResults.page,
      limit: fallbackResults.limit,
      hasMore: fallbackResults.hasMore,
      uniqueDevelopersCount: new Set(fallbackResults.results.map(a => a.developer)).size,
      source: 'client_cache',
      liveRefreshAvailable: false
    };
  }

  /**
   * Get verified category counts
   */
  async getCategoryCounts(): Promise<Record<string, number>> {
    try {
      const res = await fetch('/api/categories');
      if (res.ok) {
        const data = await res.json();
        return data.categories || {};
      }
    } catch (err) {
      console.warn('[AppCatalogService] Error fetching category counts:', err);
    }
    return {};
  }

  /**
   * Get real-time catalog stats and telemetry
   */
  async getCatalogStats(): Promise<CatalogStats | null> {
    try {
      const res = await fetch('/api/stats/catalog');
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.warn('[AppCatalogService] Error fetching catalog stats:', err);
    }
    return null;
  }

  /**
   * Run Database Migration on demand
   */
  async triggerMigration(): Promise<any> {
    try {
      const res = await fetch('/api/admin/migrate-database', { method: 'POST' });
      return await res.json();
    } catch (err: any) {
      return { status: 'error', message: err.message };
    }
  }

  /**
   * Get New Apps (New Releases, Recently Discovered, Fastest Growing)
   */
  async getNewApps(params: {
    tab?: 'new_releases' | 'recently_discovered' | 'fastest_growing';
    category?: string;
    country?: string;
    store?: string;
    q?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<{
    tab: string;
    results: AppData[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  }> {
    const {
      tab = 'new_releases',
      category = 'all',
      country = this.selectedCountry,
      store = 'all',
      q = '',
      page = 1,
      limit = 24
    } = params;

    try {
      const queryParams = new URLSearchParams({
        tab,
        category,
        country,
        store,
        q,
        page: String(page),
        limit: String(limit)
      });

      const res = await fetch(`/api/new-apps?${queryParams.toString()}`);
      if (res.ok) {
        const data = await res.json();
        return data;
      }
    } catch (err) {
      console.warn('[AppCatalogService] Error fetching new apps from API:', err);
    }

    return {
      tab,
      results: [],
      total: 0,
      page,
      limit,
      hasMore: false
    };
  }

  /**
   * Get Launch Radar Apps
   */
  async getLaunchRadar(params: {
    category?: string;
    country?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<{
    results: AppData[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
    provenance?: string;
  }> {
    const { category = 'all', country = this.selectedCountry, page = 1, limit = 20 } = params;

    try {
      const queryParams = new URLSearchParams({
        category,
        country,
        page: String(page),
        limit: String(limit)
      });

      const res = await fetch(`/api/launch-radar?${queryParams.toString()}`);
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.warn('[AppCatalogService] Error fetching launch radar:', err);
    }

    return {
      results: [],
      total: 0,
      page,
      limit,
      hasMore: false,
      provenance: 'AppStatsView Derived Intelligence'
    };
  }

  /**
   * Get Continuous Discovery Engine Stats
   */
  async getDiscoveryStats(): Promise<any> {
    try {
      const res = await fetch('/api/discovery/stats');
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.warn('[AppCatalogService] Error fetching discovery stats:', err);
    }
    return null;
  }

  /**
   * Get debug catalog telemetry
   */
  async getDebugCatalog(): Promise<any> {
    try {
      const res = await fetch('/api/debug/catalog');
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.warn('[AppCatalogService] Error fetching debug catalog:', err);
    }
    return null;
  }
}

export const appCatalog = new AppCatalogService();
